// src/Pages/Localizacao.tsx
import {
  Box,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { LocationOn as LocationOnIcon } from "@mui/icons-material";
import React, { ChangeEvent, useState, useEffect } from "react";
import { FormData } from "..";
import { getCidadesByUF } from "./Cidades";

/** ======= MAPA DE NOMES DE ESTADOS PARA SIGLAS ======= */
const UF_BY_STATE: Record<string, string> = {
  "Acre": "AC",
  "Alagoas": "AL",
  "Amapá": "AP",
  "Amazonas": "AM",
  "Bahia": "BA",
  "Ceará": "CE",
  "Distrito Federal": "DF",
  "Espírito Santo": "ES",
  "Goiás": "GO",
  "Maranhão": "MA",
  "Mato Grosso": "MT",
  "Mato Grosso do Sul": "MS",
  "Minas Gerais": "MG",
  "Pará": "PA",
  "Paraíba": "PB",
  "Paraná": "PR",
  "Pernambuco": "PE",
  "Piauí": "PI",
  "Rio de Janeiro": "RJ",
  "Rio Grande do Norte": "RN",
  "Rio Grande do Sul": "RS",
  "Rondônia": "RO",
  "Roraima": "RR",
  "Santa Catarina": "SC",
  "São Paulo": "SP",
  "Sergipe": "SE",
  "Tocantins": "TO",
};

/** Siglas válidas — a mesma lista alimenta o Select e a normalização abaixo. */
const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT",
  "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO",
  "RR", "SC", "SP", "SE", "TO",
];

const UF_PADRAO = "RS";

/** Sigla válida em maiúsculas, ou "" para nulo/vazio/lixo (ex.: "nu", "null"). */
function siglaValida(valor?: string | null): string {
  const sigla = String(valor ?? "").trim().toUpperCase();
  return UFS.includes(sigla) ? sigla : "";
}

interface Props {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  formValid: boolean;
  setFormValid: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Localizacao = ({
  formData,
  setFormData,
  formValid,
  setFormValid,
}: Props) => {
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [loadingLocation, setLoadingLocation] = useState(false);

  /**
   * UF que a tela realmente usa.
   *
   * A maioria dos cadastros antigos tem `uf` nulo no banco, e o Select mostrava
   * "RS" por um fallback só VISUAL (`formData.uf || "RS"`) — o formData seguia
   * nulo. Como a lista de cidades olhava o formData, ela vinha vazia: o estado
   * parecia escolhido mas não tinha cidade nenhuma, e só aparecia depois de
   * trocar de estado e voltar, que era quando o formData enfim recebia valor.
   */
  const uf = siglaValida(formData.uf) || UF_PADRAO;

  /**
   * Mantém o formData igual ao que está na tela.
   *
   * Sem isso, além das cidades vazias, o salvar mandava `String(null)` — e como
   * USUARIO.UF é char(2), o banco truncava para "nu". É de onde vieram os
   * registros com uf = 'nu'.
   */
  useEffect(() => {
    if (formData.uf !== uf) {
      setFormData((prev) => ({ ...prev, uf }));
    }
  }, [uf, formData.uf, setFormData]);

  /** ======= HANDLE CHANGE PADRÃO ======= */
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "zonaRural") {
        return {
          ...prev,
          zonaRural: value === "true",
          endereco: "",
          bairro: "",
          localidadeRural: "",
          complemento: "",
        };
      }

      if (name === "uf") {
        return {
          ...prev,
          uf: value,
          cidade: "", // zera cidade quando muda UF
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  /** ======= OBTÉM LOCALIZAÇÃO ATUAL ======= */
  const obterLocalizacao = () => {
    setLoadingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            if (data.address) {
              const addr = data.address;
              const ufDetectada =
                addr.state_code || UF_BY_STATE[addr.state] || "";
              const cidadeDetectada =
                addr.city || addr.town || addr.village || "";

              setFormData((prev) => ({
                ...prev,
                cep: addr.postcode || "",
                cidade: cidadeDetectada,
                uf: ufDetectada,
                bairro: addr.suburb || addr.village || "",
                endereco: addr.road || "",
              }));
            }
          } catch (error) {
            console.error("Erro ao obter localização", error);
          }
          setLoadingLocation(false);
        },
        (error) => {
          console.error("Erro ao acessar localização", error);
          setLoadingLocation(false);
        }
      );
    } else {
      console.error("Geolocalização não suportada no navegador.");
      setLoadingLocation(false);
    }
  };

  /** ======= VALIDAÇÃO ======= */
  useEffect(() => {
    const errors: { [key: string]: string } = {};
    if (!formData.cidade) errors.cidade = "Cidade é obrigatória";
    if (!formData.cep || !/^\d{5}-?\d{3}$/.test(formData.cep))
      errors.cep = "CEP inválido.";

    setFormErrors(errors);
    setFormValid(Object.keys(errors).length === 0);
  }, [formData, setFormValid]);

  /** ======= LISTA DE CIDADES ======= */
  const cidades = React.useMemo(() => getCidadesByUF(uf), [uf]);

  /** ======= NORMALIZAÇÃO DE STRING ======= */
  const normalize = (s: string) =>
    s?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() || "";

  /** ======= RENDER ======= */
  return (
    <Box>
      <Box display="flex" justifyContent="center" my={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={obterLocalizacao}
          disabled={loadingLocation}
          startIcon={<LocationOnIcon />}
        >
          {loadingLocation ? (
            <CircularProgress size={24} />
          ) : (
            "Carregar Localização Atual"
          )}
        </Button>
      </Box>

      <Grid container spacing={2}>
        {/* TIPO DE ZONA */}
        <Grid item xs={12}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Localização</InputLabel>
            <Select
              name="zonaRural"
              value={formData.zonaRural ? "true" : "false"}
              onChange={handleChange}
            >
              <MenuItem value="false">Zona Urbana</MenuItem>
              <MenuItem value="true">Zona Rural</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* UF */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>UF</InputLabel>
            <Select name="uf" value={uf} onChange={handleChange}>
              {UFS.map((estado) => (
                <MenuItem key={estado} value={estado}>
                  {estado}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* CIDADE */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            key={`cidade-${uf}`} // força recriar quando muda UF
            options={cidades}
            value={formData.cidade || ""}
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                cidade: newValue || "",
              }));
            }}
            isOptionEqualToValue={(opt, val) => normalize(opt) === normalize(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cidade"
                margin="normal"
                error={!!formErrors.cidade}
                helperText={formErrors.cidade}
                fullWidth
              />
            )}
          />
        </Grid>

        {/* CEP */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="CEP"
            name="cep"
            value={formData.cep}
            onChange={handleChange}
            margin="normal"
            inputProps={{ maxLength: 10 }}
            error={!!formErrors.cep}
            helperText={formErrors.cep}
          />
        </Grid>

        {/* CAMPOS URBANOS */}
        {!formData.zonaRural && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bairro"
                name="bairro"
                value={formData.bairro}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Endereço"
                name="endereco"
                value={formData.endereco}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Complemento"
                name="complemento"
                value={formData.complemento}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
          </>
        )}

        {/* CAMPO RURAL */}
        {formData.zonaRural && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Localidade Rural"
              name="localidadeRural"
              value={formData.localidadeRural}
              onChange={handleChange}
              margin="normal"
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
